/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { PriceComponent } from '@msdyn365-commerce/components';
import { ICoreContext, IImageSettings, Image, ITelemetry } from '@msdyn365-commerce/core';
import { ProductListLine } from '@msdyn365-commerce/retail-proxy';
import { ProductCatalog, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions, getFallbackImageUrl, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import { IOrderTemplateLineViewModel } from '../gdn-order-template';
import OrderTemplateQuantity from './common/gdn-order-template-quantity';

export interface IOrderTemplateLineProps extends ProductListLine {
    orderTemplateLine: IOrderTemplateLineViewModel;
    productListLine: ProductListLine;
    price?: ProductPrice;
    context: ICoreContext;
    productUrl: string;
    imageSettings?: IImageSettings;
    telemetry: ITelemetry;
    moduleId: string;
    moduleTypeName: string;
    quantity: number;
    isQuantityDisabledForUpdate?: boolean;
    handlers: {
        onAddToBag(product: SimpleProduct, quantity: number, itemKey: string, catalogId?: number | undefined): void;
        onRemoveItem(line: IOrderTemplateLineViewModel): void;
        onChangeQuantity(orderTemplateLineId: string, quantity: number, props?: IOrderTemplateLineProps): void;
        onSelect(line: IOrderTemplateLineViewModel, isChecked: boolean): void;
    };
    resources: {
        selectOneRowText?: string;
        moreButtonText?: string;
        viewProductDetailsText?: string;
        productDimensionTypeColor: string;
        productDimensionTypeSize: string;
        productDimensionTypeStyle: string;
        originalPriceText: string;
        currentPriceText: string;
        freePriceText: string;
        inputQuantityAriaLabel: string;
        removeFromOrderTemplateLinesText: string;
        addToBagButtonText: string;
        deleteButtonText: string;
        incrementButtonAriaLabel?: string;
        decrementButtonAriaLabel?: string;
        editOrderTemplateLineQuantityError?: string;
        unavailableProductErrorMessage?: string;
        outOfStockProductErrorMessage?: string;
    };
    index: number;
    isMobile: boolean;
    productQuantityView?: React.ReactNode;
    addToBagButton?: React.ReactNode;
    removeButton?: React.ReactNode;
    toggleDetailsDisplayed(props: IOrderTemplateLineProps): void;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogs?: ProductCatalog[];
    error?: string;
    shouldDisable?: boolean;
}

export interface IOrderTemplateLineViewProps {
    key?: string;
    productImage: React.ReactNode;
    productPrice?: React.ReactNode;
    addToBagButton?: React.ReactNode;
    removeButton?: React.ReactNode;
    productName?: string;
    productNumber?: React.ReactNode;
    productInfo?: React.ReactNode;
    productLabel?: React.ReactNode;
    productUnitOfMeasure?: React.ReactNode;
    productQuantity?: number;
    productQuantityView?: React.ReactNode;
    productListId?: string;
    selectLine?: React.ReactNode;
    popUp?: JSX.Element;
    extraActionsPopup?: JSX.Element | null | undefined;
    isMobile?: boolean;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    productLineCatalog?: React.ReactNode;
    disabled?: boolean;
}

interface IEventHandlerFactory {
    getAddToBag(): (event: React.MouseEvent<HTMLElement>) => void;
    getRemoveItem(): (event: React.MouseEvent<HTMLElement>) => void;
}

const OrderTemplateLineActions = (props: IOrderTemplateLineProps): IEventHandlerFactory => {
    const { orderTemplateLine, productListLine, quantity, handlers } = props;
    const { LineId, CatalogId } = productListLine;
    const { onAddToBag, onRemoveItem } = handlers;

    return {
        getAddToBag: () => (event: React.MouseEvent<HTMLElement>) => {
            LineId &&
                orderTemplateLine.fullProduct &&
                onAddToBag(orderTemplateLine.fullProduct.ProductDetails, quantity, LineId, CatalogId);
        },
        getRemoveItem: () => (event: React.MouseEvent<HTMLElement>) => LineId && onRemoveItem(props.orderTemplateLine)
    };
};

interface IPopupProps extends IOrderTemplateLineProps {
    addButtonClassName: string;
    removeButtonClassName: string;
}

const PopUp = (props: IPopupProps): JSX.Element | null => {
    const { addToBagButtonText, removeFromOrderTemplateLinesText, moreButtonText, viewProductDetailsText } = props.resources;
    const [showMobile, setShowMobile] = useState(false);
    const componentClassName = 'ms-table';

    const getViewHandler = () => (event: React.MouseEvent<HTMLElement>) => {
        props.toggleDetailsDisplayed(props);
    };

    const toggle = React.useCallback(() => {
        setShowMobile(!showMobile);
    }, [showMobile]);

    return (
        <div className={`${componentClassName}__row-links-minified`}>
            <Button
                className={`${componentClassName}__row-links-toggle`}
                aria-label={moreButtonText}
                data-type={props.index}
                onClick={toggle}
            />
            {showMobile && (
                <div className={`${componentClassName}__row-links`}>
                    <Button
                        className='ms-table__row-links-view'
                        onClick={getViewHandler()}
                        title={viewProductDetailsText}
                        aria-label={viewProductDetailsText}
                    >
                        View
                    </Button>
                    {getActionButton(
                        props.isMobile,
                        props.removeButtonClassName,
                        removeFromOrderTemplateLinesText,
                        OrderTemplateLineActions(props).getRemoveItem()
                    )}
                    {getActionButton(
                        props.isMobile,
                        props.addButtonClassName,
                        addToBagButtonText,
                        OrderTemplateLineActions(props).getAddToBag()
                    )}
                </div>
            )}
        </div>
    );
};

const getActionButton = (
    isMobile: boolean,
    className: string,
    buttonText: string,
    clickHandler: (event: React.MouseEvent<HTMLElement>) => void
): React.ReactNode => (
    <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
        {isMobile ? buttonText : ''}
    </Button>
);

const getDesktopActionButton = (
    className: string,
    buttonText: string,
    clickHandler: (event: React.MouseEvent<HTMLElement>) => void
): React.ReactNode => (
    <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
        {buttonText}
    </Button>
);
/**
 * On Select line function.
 * @param orderTemplateLine -Order template line.
 * @param input -Order template line props.
 * @returns Set on select order template.
 */
const onSelectLineHandler = (orderTemplateLine: IOrderTemplateLineViewModel, input: IOrderTemplateLineProps) => (
    event: React.ChangeEvent<HTMLInputElement>
) => {
    orderTemplateLine.isChecked = !orderTemplateLine.isChecked;

    input.handlers.onSelect(orderTemplateLine, event.target.checked);
};

export const OrderTemplateLineView = (input: IOrderTemplateLineProps): IOrderTemplateLineViewProps | null => {
    const {
        orderTemplateLine,
        productListLine,
        price,
        context,
        imageSettings,
        quantity,
        resources,
        isMobile,
        isMultipleCatalogFeatureEnabledInHq
    } = input;
    const product = orderTemplateLine.fullProduct;
    const { LineId, UnitOfMeasure } = productListLine;
    const { removeFromOrderTemplateLinesText, addToBagButtonText, selectOneRowText } = resources;
    if (!product) {
        return null;
    }

    const { ItemId, PrimaryImageUrl } = product.ProductDetails;
    const fallbackImage = getFallbackImageUrl(
        ItemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );
    const addButtonClassName = input.isMobile ? 'ms-table__row-links-add' : 'ms-order-template-table-line__product-add-button';
    const removeButtonClassName = input.isMobile ? 'ms-table__row-links-delete' : 'ms-order-template-table-line__product-remove-button';

    return {
        isMobile,
        isMultipleCatalogFeatureEnabledInHq: isMultipleCatalogFeatureEnabledInHq,
        selectLine: (
            <label className='checkbox-container'>
                <input
                    aria-label={selectOneRowText}
                    className='checkbox-input'
                    type='checkbox'
                    checked={orderTemplateLine.isChecked}
                    aria-checked={orderTemplateLine.isChecked}
                    onChange={onSelectLineHandler(orderTemplateLine, input)}
                />
                <span className='checkmark' />
            </label>
        ),
        key: LineId!,
        productLineCatalog: isMultipleCatalogFeatureEnabledInHq && renderProductCatalog(input),
        productImage: _renderProductImage(
            'ms-order-template-table-line__product-image',
            context,
            product.ProductDetails.Name!,
            PrimaryImageUrl,
            fallbackImage,
            imageSettings
        ),
        productInfo: renderProductInfo(input),
        productPrice: price && _renderPrice(input),
        productNumber: product && (
            <>
                <div className='ms-order-template-table-line__product-number'>{ItemId}</div>
                {input.error !== '' ? (
                    <div className='ms-order-template-table-line__product-availability'>{_renderProductError(input)}</div>
                ) : (
                    undefined
                )}
            </>
        ),
        productUnitOfMeasure: price ? (
            <div className='ms-order-template-table-line__product-unit-of-measure'>{UnitOfMeasure || price.UnitOfMeasure}</div>
        ) : (
            undefined
        ),
        productListId: LineId!,
        removeButton:
            !isMultipleCatalogFeatureEnabledInHq &&
            getActionButton(
                input.isMobile,
                removeButtonClassName,
                removeFromOrderTemplateLinesText,
                OrderTemplateLineActions(input).getRemoveItem()
            ),
        productQuantityView: <div>{_generateQuantity(input, LineId!)}</div>,
        productQuantity: quantity,
        addToBagButton:
            !isMultipleCatalogFeatureEnabledInHq &&
            getActionButton(input.isMobile, addButtonClassName, addToBagButtonText, OrderTemplateLineActions(input).getAddToBag()),
        extraActionsPopup:
            !isMobile && isMultipleCatalogFeatureEnabledInHq ? (
                <ExtraActionsPopup {...input} addButtonClassName={addButtonClassName} removeButtonClassName={removeButtonClassName} />
            ) : (
                undefined
            ),
        popUp: <PopUp {...input} addButtonClassName={addButtonClassName} removeButtonClassName={removeButtonClassName} />,
        disabled: input.shouldDisable
    };
};

const ExtraActionsPopup = (props: IPopupProps): JSX.Element | null => {
    const [showActions, setShowActions] = useState(false);
    const toggle = React.useCallback(() => {
        setShowActions(!showActions);
    }, [showActions]);

    const className = 'ms-order-template-table-line__extra-actions-cell';
    const actionsContainerClassName = `${className}__actions-container`;
    return (
        <div className={className}>
            <Button className={`${className}__toggle`} aria-label={''} onClick={toggle} />
            {showActions && (
                <div className={actionsContainerClassName}>
                    {getDesktopActionButton(
                        'ms-order-template-table-lines__products-add-button',
                        'Add to bag',
                        OrderTemplateLineActions(props).getAddToBag()
                    )}
                    {getDesktopActionButton(
                        'ms-order-template-table-lines__products-remove-button',
                        'Remove item',
                        OrderTemplateLineActions(props).getRemoveItem()
                    )}
                </div>
            )}
        </div>
    );
};

export const OrderTemplateMobileLineView = (input: IOrderTemplateLineProps): IOrderTemplateLineViewProps | undefined => {
    const { orderTemplateLine, productListLine, price, context, imageSettings, isMultipleCatalogFeatureEnabledInHq } = input;
    const product = orderTemplateLine.fullProduct;
    const { LineId, UnitOfMeasure } = productListLine;
    if (!product) {
        return;
    }
    const { ItemId, PrimaryImageUrl } = product.ProductDetails;
    const fallbackImage = getFallbackImageUrl(
        ItemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );

    return {
        productLineCatalog: isMultipleCatalogFeatureEnabledInHq && renderProductCatalog(input),
        productImage: _renderProductImage(
            'thumbnail',
            context,
            product.ProductDetails.Name!,
            PrimaryImageUrl,
            fallbackImage,
            imageSettings
        ),
        productInfo: renderProductInfo(input),
        productPrice: price && _renderPrice(input),
        productNumber: ItemId,
        productUnitOfMeasure: UnitOfMeasure || price?.UnitOfMeasure,
        productQuantityView: _generateQuantity(input, LineId!)
    };
};

export interface IOrderTemplateProductQuantityViewProps {
    ContainerProps: INodeProps;
    input: React.ReactNode;
}

/**
 * On Change function.
 * @param onChangeQuantity -On change quantity function.
 * @param itemKey -String.
 * @param props -Order template line props.
 * @returns Set change quantity.
 */
const onChangeHandler = (
    onChangeQuantity: (orderTemplateLineId: string, quantity: number, props?: IOrderTemplateLineProps | undefined) => void,
    itemKey: string,
    props: IOrderTemplateLineProps
) => (newValue: number) => {
    if (onChangeQuantity) {
        onChangeQuantity(itemKey, newValue, props);
    }
};

const _generateQuantity = (props: IOrderTemplateLineProps, itemKey: string): React.ReactNode => {
    const { handlers, resources, quantity } = props;
    const { onChangeQuantity } = handlers;
    return (
        <OrderTemplateQuantity
            currentCount={quantity}
            onChange={onChangeHandler(onChangeQuantity, itemKey, props)}
            max={999_999_999}
            inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
            incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
            decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
            disabled={props.isQuantityDisabledForUpdate}
        />
    );
};

export const _renderProductImage = (
    className: string,
    context: ICoreContext,
    heading: string,
    image?: string,
    fallbackImage?: string,
    imageSettings?: IImageSettings
): JSX.Element | null => {
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            lg: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            xl: { q: 'w=64&h=64&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    return (
        <Image
            requestContext={context?.actionContext?.requestContext}
            className={className}
            altText={heading}
            title={heading}
            src={image}
            fallBackSrc={fallbackImage}
            gridSettings={context?.request?.gridSettings!}
            imageSettings={imageSettings || defaultImageSettings}
            loadFailureBehavior='empty'
            tabIndex={0}
        />
    );
};

export const renderProductCatalog = (props: IOrderTemplateLineProps) => {
    const catalogId = props.productListLine.CatalogId;
    validateCatalogId(catalogId);

    if (!props.context.request.user.isB2b || !catalogId || !ArrayExtensions.hasElements(props.catalogs)) {
        return null;
    }

    const catalog = props.catalogs.find(item => item.RecordId === catalogId);

    if (!catalog || !catalog.Name) {
        return null;
    }

    return <div className='msc-cart-line__catalog-label'>{catalog.Name}</div>;
};

const _renderPrice = (props: IOrderTemplateLineProps): JSX.Element | null => {
    const { price, context, moduleId, moduleTypeName, resources } = props;

    if (price) {
        return (
            <PriceComponent
                id={moduleId}
                typeName={moduleTypeName}
                data={{ price }}
                className='ms-order-template-table-line__product-price'
                freePriceText={resources.freePriceText}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                context={context}
            />
        );
    }
    return null;
};

const _renderProductDimensions = (props: IOrderTemplateLineProps): (string | undefined)[] => {
    const { orderTemplateLine } = props;

    if (!orderTemplateLine.fullProduct || !orderTemplateLine.fullProduct.ProductDetails.Dimensions) {
        return [];
    }

    return orderTemplateLine.fullProduct.ProductDetails.Dimensions.map(dimension => {
        if (!dimension.DimensionValue || !dimension.DimensionValue.Value) {
            return undefined;
        }

        return dimension.DimensionValue.Value;
    });
};

const _renderProductError = (props: IOrderTemplateLineProps) => {
    const { error } = props;
    if (!error) {
        return '';
    }
    return error;

    // const errorMessages = {
    //     OOS: resources.outOfStockProductErrorMessage,
    //     UNASSORTED: resources.unavailableProductErrorMessage,
    //     NONE: ''
    // };

    // return errorMessages[error];
};

export const renderProductInfo = (props: IOrderTemplateLineProps): React.ReactNode | undefined => {
    const { orderTemplateLine, productUrl } = props;
    const name = orderTemplateLine.fullProduct?.ProductDetails.Name;

    const dimensions = _renderProductDimensions(props);
    if (!orderTemplateLine.fullProduct) {
        return [];
    }

    const productName = productUrl ? (
        <a className='ms-order-template-table-line__product-info-name' href={productUrl}>
            {name}
        </a>
    ) : (
        <div className='ms-order-template-table-line__product-info-name'>{name}</div>
    );

    const dimensionsLine: string = dimensions.join(', ');

    return (
        <div className='ms-order-template-table-line__product-info'>
            <div className='ms-order-template-table-line__product-info-name'>{productName}</div>
            <div className='ms-order-template-table-line__product-info-dimension'>{dimensionsLine}</div>
        </div>
    );
};
